"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { nanoid } from "nanoid";
import HomeChatMessages from "./HomeChatMessages";
import HomeChatComposer from "./HomeChatComposer";

const LS_KEY = "tutoria_home_chat_v1";
const SESSION_TTL_MS = 1000 * 60 * 60 * 12; // 12h

const INITIAL_ASSISTANT_MSG =
  "Hola 👋 Soy el asistente de TutorIA+. Puedo ayudarte con dudas sobre la plataforma, planes y cómo empezar.";

function now() {
  return Date.now();
}

function loadPersisted() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePersisted(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {}
}

function clearPersisted() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {}
}

function buildInitialInteractions() {
  return [
    {
      id: nanoid(),
      role: "assistant",
      message: INITIAL_ASSISTANT_MSG,
    },
  ];
}

export default function HomeChatLayout({ onClose }) {
  const bottomRef = useRef(null);
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  // evita que el primer render “pise” localStorage antes de hidratar
  const [hydrated, setHydrated] = useState(false);

  const [interactions, setInteractions] = useState(buildInitialInteractions());
  const [isSending, setIsSending] = useState(false);
  const [homeChatSessionId, setHomeChatSessionId] = useState(null);

  // scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [interactions]);

  // modal lock
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
    };
  }, []);

  // hydrate from localStorage (NO crea sesión backend aquí)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const persisted = loadPersisted();
    const expired =
      !persisted?.updatedAt || now() - persisted.updatedAt > SESSION_TTL_MS;

    if (persisted && !expired) {
      if (
        Array.isArray(persisted.interactions) &&
        persisted.interactions.length
      ) {
        setInteractions(persisted.interactions);
      } else {
        setInteractions(buildInitialInteractions());
      }

      setHomeChatSessionId(persisted.homeChatSessionId || null);
    } else {
      // expiró -> borramos todo y arrancamos limpio
      clearPersisted();
      setHomeChatSessionId(null);
      setInteractions(buildInitialInteractions());
    }

    setHydrated(true);
  }, []);

  // persist (solo después de hidratar)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hydrated) return;

    savePersisted({
      homeChatSessionId,
      interactions,
      updatedAt: now(),
    });
  }, [hydrated, interactions, homeChatSessionId]);

  const sendToBackend = useCallback(
    async (text) => {
      const res = await fetch(`${apiUrl}/home-chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          homeChatSessionId, // puede ser null
          message: text,
        }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Error al enviar mensaje.");
      }

      const data = await res.json();

      // si el backend creó sesión, nos devuelve el id
      if (
        data.homeChatSessionId &&
        data.homeChatSessionId !== homeChatSessionId
      ) {
        setHomeChatSessionId(data.homeChatSessionId);
      }

      return data.reply || "";
    },
    [apiUrl, homeChatSessionId]
  );

  async function handleSendMessage(text) {
    if (isSending) return;

    // edge case: si el usuario escribe antes de hidratar
    if (!hydrated) setHydrated(true);

    setInteractions((prev) => [
      ...prev,
      { id: nanoid(), role: "user", message: text },
      { id: nanoid(), role: "assistant", message: "" },
    ]);

    setIsSending(true);

    try {
      const reply = await sendToBackend(text);

      setInteractions((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], message: reply };
        return copy;
      });
    } catch (err) {
      const fallback =
        "Tuve un problema conectándome al servidor. Intenta nuevamente en unos segundos.";

      setInteractions((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], message: fallback };
        return copy;
      });
    } finally {
      setIsSending(false);
    }
  }

  function handleNewChat() {
    if (isSending) return;

    clearPersisted();
    setHomeChatSessionId(null);
    setInteractions(buildInitialInteractions());
    setHydrated(true);
  }

  return (
    <>
      <div onClick={onClose} className="home-chat-overlay" />

      <div className="home-chat-modal">
        <div className="home-chat-header">
          <h6 className="mb-0">Asistente de TutorIA+</h6>

          <div className="buttons">
            <div className="btn-grp">
              <div className="left-side-btn dropup">
                <button
                  onClick={handleNewChat}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Nuevo chat"
                  className="react-btn btn-default btn-small btn-border hover-btn"
                  aria-label="Nuevo chat"
                  disabled={isSending}
                >
                  <i className="fa-regular fa-rotate-right"></i>
                </button>

                <button
                  onClick={onClose}
                  className="react-btn btn-default btn-small btn-border hover-btn btn-close-mobile"
                  aria-label="Cerrar chat"
                >
                  <i className="fa-regular fa-xmark"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="chat-box-section home-chat-messages">
          <HomeChatMessages interactions={interactions} isSending={isSending} />
          <div ref={bottomRef} />
        </div>

        <HomeChatComposer
          onSendMessage={handleSendMessage}
          isSending={isSending}
        />
      </div>
    </>
  );
}
